import type { CrosswordSettings, CrosswordStyle } from '@/components/DesignStudio/CrosswordProperties/types';
import type { SignedUrlResponse } from './typings/image';
import api from './baseApi';

const storefrontBaseUrl = process.env.VUE_APP_CUSTOMILY_STOREFRONT_SERVICE_API_URL;

export interface CrosswordTexture {
    Path: string;
    Name: string;
    /** Crossword preview of the tile; falls back to the tile itself when it is missing */
    Thumbnail: string | null;
}

export const getCrosswordTextures = (): Promise<CrosswordTexture[]> =>
    api.get<CrosswordTexture[]>('/Crossword/Textures').then(({ data }) => data);

export const getCrosswordTileUploadSignedUrl = (filename: string): Promise<SignedUrlResponse> =>
    api
        .get<SignedUrlResponse>(`/Crossword/Tiles/UploadSignedUrl/${encodeURIComponent(filename)}`)
        .then(({ data }) => data);

// The service resolves the paths itself, both the tile and the font travel as server paths
const toServiceStyle = (style: CrosswordStyle) => ({
    tileImageUrl: style.tileImage,
    font: style.font,
    fontColor: style.fontColor,
    fontSize: style.fontSize
});

/**
 * Generates the crossword through the storefront service. Legacy placeholders (the ones
 * configured with CrosswordConfig instead of styles) still go to crossword.customily.com.
 */
export const generateCrosswordImage = async (
    words: string[],
    settings: CrosswordSettings
): Promise<{ base64: string }> => {
    // alternateStyles only drives which styles we send, the generator doesn't know about it
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { alternateStyles, ...options } = settings;

    const body = {
        words,
        options: {
            ...options,
            // Templates configured before the scrabble mode toggle existed always rendered letter values
            showLetterValues: settings.showLetterValues ?? true,
            styles: settings.styles.map(toServiceStyle)
        }
    };

    const response = await fetch(`${storefrontBaseUrl}/scrabble-generator/generate`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        let messageValue = '';

        try {
            const error = await response.json();
            try {
                const parsedMessage = JSON.parse(error.message) as {
                    error: { message: string };
                };
                messageValue = parsedMessage?.error?.message;
            } catch {
                messageValue = error.message;
            }
        } catch {
            // Backend returned non-JSON (HTML/plain text)
            messageValue = await response.text();
        }

        throw new Error(`Failed to generate crossword: ${messageValue || response.statusText}`);
    }

    return await response.json();
};
